import React from "react";
import { ColumnDef, SortingState, Updater } from "@tanstack/react-table";
import {
  Performance as PerformanceIcon,
  Table as TableIcon
} from "@dreamhub/designsystem/icons";
import DataTable from "@/components/controls/DataTable/DataTable";
import DreamerAttachmentFilterToolbar from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentFilterToolbar";
import DreamerAttachmentModalShell, {
  DreamerModalHeaderSlots
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentModalShell";
import DreamerAttachmentEmptyState, {
  DreamerAttachmentEmptyVariant
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentEmptyState";
import ShowAsDropdown, {
  ShowAsOption
} from "@/components/global/Chat/ShowAsDropdown";
import { DreamerTableRow } from "@/entities/attachments/types";
import { Filters, FiltersSetter } from "@/services/api/types";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";

type TableModalViewMode = "table" | "chart";

const VIEW_MODE_OPTIONS: ShowAsOption<TableModalViewMode>[] = [
  { mode: "table", icon: <TableIcon />, labelKey: "chat.viewModes.table" },
  {
    mode: "chart",
    icon: <PerformanceIcon />,
    labelKey: "chat.viewModes.chart",
    disabled: true,
    disabledTooltipKey: "chat.tooltips.chartUnavailable"
  }
];

interface ExpandedDreamerTableModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  columns: ColumnDef<DreamerTableRow, unknown>[];
  data: DreamerTableRow[];
  generatedAt?: string;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  filters?: Filters;
  setFilters?: FiltersSetter;
  filterOptions?: DataTableFilter[];
  emptyVariant?: DreamerAttachmentEmptyVariant | null;
  onDownload?: () => void;
  isDownloading?: boolean;
  canDownload?: boolean;
  headerSlots?: DreamerModalHeaderSlots;
}

const ExpandedDreamerTableModal: React.FC<ExpandedDreamerTableModalProps> = ({
  isOpen,
  setIsOpen,
  title,
  columns,
  data,
  generatedAt,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  sorting,
  onSortingChange,
  filters,
  setFilters,
  filterOptions = [],
  emptyVariant,
  onDownload,
  isDownloading = false,
  canDownload = true,
  headerSlots
}) => (
  <DreamerAttachmentModalShell
    isOpen={isOpen}
    setIsOpen={setIsOpen}
    title={title}
    {...headerSlots}
    onDownload={onDownload}
    isDownloading={isDownloading}
    canDownload={canDownload}
    generatedAt={generatedAt}
  >
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <div className="flex justify-between items-center gap-2 flex-wrap shrink-0">
        {filters && setFilters ? (
          <DreamerAttachmentFilterToolbar
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
          />
        ) : (
          <div />
        )}
        <ShowAsDropdown
          selectedMode="table"
          options={VIEW_MODE_OPTIONS}
          // table is the only enabled option, so onSelect is a no-op.
          onSelect={() => undefined}
          dataTest="table-modal-show-as-trigger"
        />
      </div>
      <div className="flex-1 min-h-0 min-w-0 flex flex-col border border-border-border1 rounded-2xl overflow-hidden">
        <DataTable
          columns={columns}
          data={data}
          firstColumnWidth={200}
          headerClassName="border-b border-t-0 border-border-border1 bg-surface-elevation1"
          className="w-full flex-1 min-h-0"
          isInfiniteScroll
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          isDataLoading={isLoading}
          manualSorting
          initialSortBy={sorting}
          onSortingChange={onSortingChange}
          customEmptyState={
            emptyVariant ? (
              <DreamerAttachmentEmptyState variant={emptyVariant} />
            ) : undefined
          }
        />
      </div>
    </div>
  </DreamerAttachmentModalShell>
);

export default ExpandedDreamerTableModal;
